import ComingSoon from "@/components/homepage/ComingSoon";
import { RoundCard } from "@/components/homepage/RoundCard";
import { SelectLeague } from "@/components/homepage/SelectLeague";
import SelectRoundTab from "@/components/homepage/SelectRoundTab";
import SelectSport from "@/components/homepage/SelectSport";
import { SelectSortSport, SortSportEnum } from "@/components/homepage/SortSport";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleError } from "@/hooks/useHandleError";
import { useHomePageStore, useSkipPrizesStore } from "@/store";
import { useSoccerRounds } from "@roman/shared/hooks/soccer/useSoccerRounds";
import { minBy } from "lodash";
import { type HTMLAttributes, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HIGH_PRIZE_POOL, LEAGUES, type League } from "./constant";

export const Tournaments = () => {
  const selectedLeague = useHomePageStore((state) => state.selectedLeague);
  const setSelectedSortType = useHomePageStore((state) => state.setSelectedSortType);
  const selectedSortType = useHomePageStore((state) => state.selectedSortType);

  const selectedSport = useHomePageStore((state) => state.selectedSport);
  const setSelectedSport = useHomePageStore((state) => state.setSelectedSport);

  const selectedTab = useHomePageStore((state) => state.selectedTab);
  const setSelectedTab = useHomePageStore((state) => state.setSelectedTab);

  const { skipPrizes } = useSkipPrizesStore();

  const navigate = useNavigate();

  const handleError = useHandleError("ラウンドの取得に失敗しました");

  const { data, isLoading } = useSoccerRounds("FreePoints", handleError, "ja");

  const filteredRounds = useMemo(() => {
    switch (selectedTab.type) {
      case "all":
        if (selectedLeague.seasonId === "all") {
          return data.rounds ?? [];
        }

        return data.rounds.filter((round) => round.seasonId === selectedLeague.seasonId) || [];
      case "popular":
        return (
          data.rounds
            .filter((round) => {
              if (selectedLeague.seasonId === "all") {
                return Number.parseInt(round.prizePool) >= HIGH_PRIZE_POOL;
              }
              return round.seasonId === selectedLeague.seasonId && Number.parseInt(round.prizePool) >= HIGH_PRIZE_POOL;
            })
            .sort((a, b) =>
              a.entryCloseTime === b.entryCloseTime
                ? Number.parseInt(b.prizePool) - Number.parseInt(a.prizePool)
                : a.entryCloseTime - b.entryCloseTime,
            ) || []
        );
      default:
        return [];
    }
  }, [data.rounds, selectedLeague, selectedTab]);

  const sortedRounds = useMemo(() => {
    switch (selectedSortType) {
      case SortSportEnum.EARLIEST_EVENT_DATE:
        return filteredRounds.sort((a, b) => {
          if (a.sportEventName === b.sportEventName) {
            return a.type === "Round0001" ? 1 : -1;
          }

          if (a.entryCloseTime === b.entryCloseTime) {
            return Number.parseInt(b.prizePool) - Number.parseInt(a.prizePool);
          }

          return a.entryCloseTime - b.entryCloseTime;
        });
      case SortSportEnum.HIGHEST_PRIZE_AMOUNT:
        return filteredRounds.sort((a, b) => {
          if (a.sportEventName === b.sportEventName) {
            return a.type === "Round0001" ? 1 : -1;
          }

          return Number.parseInt(b.prizePool) - Number.parseInt(a.prizePool);
        });
      case SortSportEnum.HIGHEST_ENTRY_FEE:
        return filteredRounds.sort((a, b) => {
          if (a.sportEventName === b.sportEventName) {
            return a.type === "Round0001" ? 1 : -1;
          }

          return Number.parseInt(b.entryFee) - Number.parseInt(a.entryFee);
        });
      case SortSportEnum.LOWEST_ENTRY_FEE:
        return filteredRounds.sort((a, b) => {
          if (a.sportEventName === b.sportEventName) {
            return a.type === "Round0001" ? 1 : -1;
          }

          return Number.parseInt(a.entryFee) - Number.parseInt(b.entryFee);
        });
      case SortSportEnum.NEWEST_TOURNAMENT:
        // TODO: sort by createdAt
        return filteredRounds;
      case SortSportEnum.ROUND_0001:
        return filteredRounds.filter((round) => round.type === "Round0001");
      case SortSportEnum.ROUND_0002:
        return filteredRounds.filter((round) => round.type === "Round0002");
      default:
        return filteredRounds;
    }
  }, [filteredRounds, selectedSortType]);

  const activeLeaguesMap = useMemo(() => {
    const activeLeagues = LEAGUES.filter((league) => {
      return data.rounds.some((round) => round.seasonId === league.seasonId);
    });

    return activeLeagues.reduce(
      (acc, league) => {
        acc[league.seasonId] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [data.rounds]);

  const sortedLeagues = useMemo(() => {
    const [all, ...leagues] = LEAGUES;

    const activeLeagues = leagues.map((league) => {
      const rounds = data.rounds.filter((round) => round.seasonId === league.seasonId);
      return {
        ...league,
        entryCloseTime: minBy(rounds, "entryCloseTime")?.entryCloseTime ?? Number.POSITIVE_INFINITY,
      };
    });

    activeLeagues.sort((a, b) => a.entryCloseTime - b.entryCloseTime);

    return [all].concat(activeLeagues).map(({ seasonId, name }) => ({
      seasonId,
      name,
    })) as League[];
  }, [data.rounds]);

  return (
    <div className="w-full bg-white">
      <SelectSport selectedSport={selectedSport} setSelectedSport={setSelectedSport} />
      {selectedSport.id === 1 && (
        <SelectLeague
          selectedLeague={selectedLeague}
          sortedLeagues={sortedLeagues}
          activeLeaguesMap={activeLeaguesMap}
        />
      )}
      <div className="flex justify-between items-center p-[15px]">
        <p className="text-black font-medium">大会一覧</p>
        <SelectSortSport sortBy={selectedSortType} setSortBy={setSelectedSortType} />
      </div>
      <SelectRoundTab selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      {isLoading ? (
        <LoadingSkeleton />
      ) : selectedSport.id === 1 && sortedRounds.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 bg-blue w-full gap-4 p-4 ">
          {sortedRounds.map((round) => (
            <RoundCard
              key={round.id}
              round={round}
              onClick={() => {
                skipPrizes ? navigate(`/rounds/${round.id}/select-players`) : navigate(`/rounds/${round.id}/prizes`);
              }}
            />
          ))}
        </div>
      ) : (
        <div>
          <ComingSoon />
        </div>
      )}
    </div>
  );
};

export const LoadingSkeleton = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-4 p-4" {...props}>
      {[...Array(4)].map((_, index) => (
        <div key={index} className="flex min-w-[250px] flex-col rounded-lg text-base w-full bg-white p-1">
          <Skeleton className="h-[200px] w-full rounded-xl" />
          <div className="mt-2 flex items-center flex-row justify-start">
            <Skeleton className="w-[62px] h-[28px]" />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Skeleton className="w-[62px] h-[28px]" />
            <Skeleton className="w-[62px] h-[28px]" />
          </div>
        </div>
      ))}
    </div>
  );
};
